import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IErrorDescription } from '@ibank-globals/error';
import { LanguageService } from '@ibank-globals/language/src/services/language.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { BaseComponent } from '../../../../globals/classes/base.component';
import { ErrorHandler } from '../../../../globals/classes/errorHandler';
import { RSAKey } from '../../../../globals/classes/rsa/rsaKey';
import { IUserData, UserData } from '../../../../globals/classes/userData';
import { AppSettings } from '../../../app.settings';
import {
    ISignInRequest,
    IUserIDRequest,
    IUserStatusResponse,
    IPreferredUserExperience,
} from '../../../classes/http-requests';
import {
    IGetLoginResponse,
    IImpersonationUserStatusResponse,
    ITwoFactorAuthenticationInfo,
    TwoFactorAuthenticationInfo,
} from '../../../classes/http-responses';
import { Constants } from '../../../classes/static/constants';
import { LoginAction, LoginType } from '../../../classes/static/enums';
import { Extensions } from '../../../classes/static/extensions';
import { GoogleAnalyticsUtils } from '../../../classes/static/google-analytics-utils';
import {
    IGetPersonAccountUnlockOptionsResponse,
    UnlockAcountEmail,
    GetPersonAccountUnlockOptions,
    MailItem,
} from '../../../classes/unlock-account-email';
import { UserAuth } from '../../../classes/userAuth';
import { AutoRefreshTimerService } from '../../../services/auto-refresh-timer.service';
import { ImpersonationService } from '../../../services/impersonation.service';
import { LinkUsersService } from '../../../services/link-user.service';
import { BlockedBusinessModalComponent } from '../../blocked-business-modal/blocked-business-modal.component';
import { BusinessRegistrationInfoModalComponent } from '../../business-registration-info-modal.component/business-registration-info-modal.component';
import { ConnectProfileModalComponent } from '../../connect-profile-modal/connect-profile-modal.component';
import { CreatePasswordComponent } from '../../create-password/create-password.component';
import { ForgetCredentialsRetailModalComponent } from '../../forget-credentials-retail-modal/forget-credentials-retail-modal.component';
import { IBankCredentialsModalComponent } from '../../ibank-credentials-modal/ibank-credentials-modal.component';
import { PasswordRetrievalComponent } from '../../password-retrieval/password-retrieval.component';
import { RestoreWithAuthenticationComponent } from '../../password-retrieval/restore-with-authentication/restore-with-authentication.component';
import { SwitchLoginTransitionModalComponent } from '../../switch-login-transition-modal/switch-login-transition-modal.component';
import { TwoFAModalComponent } from '../../two-fa-modal/two-fa-modal.component';
import { CorporateRecoveryPasswordModalComponent } from '../../corporate-recovery-password-modal/corporate-recovery-password-modal.component';
import { SelfBlockedModalComponent } from '../../self-blocked-modal/self-blocked-modal.component';

@Component({
    selector: 'sign-in-form-hybrid',
    template: require('./sign-in-form-hybrid.component.html'),
})
export class SignInFormHybridComponent extends BaseComponent implements OnInit, AfterViewInit {
    @Input() public action: LoginAction;
    @Input() public loginType: LoginType;
    @Input() public title: string;
    @Input() public getLoginResponse: IGetLoginResponse;

    public isUsernameInput: boolean = true;
    public isPasswordVisible: boolean = false;
    public loginHint: string;
    public username: string;
    public password: string;
    public isCorporateUser: boolean = false;
    public isLoading = false;
    public isForgotPasswordLoading = false;
    private userData: IUserData = new UserData();
    public isBlocked: boolean;
    public isForgotPasswordClicked: boolean = false;
    public langId: string;
    public isLoginBigModalEnabled: boolean = AppSettings.isLoginBigModalEnabled;
    public isGroupUserIdsBigModalEnabled: boolean = AppSettings.isGroupUserIdsBigModalEnabled;
    public isCorporateBlockedFlowEnabled: boolean = AppSettings.isCorporateBlockedFlowEnabled;
    public dynatraceForExistingOnBoarding: boolean = AppSettings.dynatraceForExistingOnBoarding;
    public isBlockUserEnabled: boolean = AppSettings.isBlockUserFeature;
    public isRebrading = AppSettings.isRebrading;

    private rememberLogin = false;
    private request: ISignInRequest;
    private usernameRecovery: boolean = true;
    private usernameEnterEventVal!: any;
    private passwordEnterEventVal!: any;

    @ViewChild('usernameRef', { static: false }) public usernameRef: ElementRef;
    @ViewChild('passwordRef', { static: false }) public passwordRef: ElementRef;

    constructor(
        private _langSvc: LanguageService,
        private elRef: ElementRef,
        private route: ActivatedRoute,
        private modalService: NgbModal,
        private _router: Router
    ) {
        super();
    }

    public get showRetail(): boolean {
        return this.loginType == LoginType.Retail;
    }

    public get showCorporate(): boolean {
        return this.loginType == LoginType.Corporate;
    }

    public get showLoginTypeSelection(): boolean {
        return this.loginType == null;
    }

    public ngOnInit() {
        this.isLoading = false;

        UserData.isCorporate$.subscribe((isCorporate) => (this.isCorporateUser = isCorporate));
        this.initializeSignInRequest();
        this._initSubscriptions();

        if (this.getLoginResponse && this.getLoginResponse.username) {
            this.loginHint = this.getLoginResponse.username;
            this.username = this.loginHint;
            this.isUsernameInput = false;

            // login_hint pre-fills the username and skips the Continue button, so
            // callUserStatusService never runs and userType is never populated.
            // Without this, Forgot Password on a business landing page falls back
            // to isCorporateUser=false and opens the retail PasswordRetrievalComponent
            // instead of CorporateRecoveryPasswordModalComponent. Reset happens via
            // variablesAtUsernameInput (back-arrow), after which a real userstatus
            // will set the accurate value.
            if (this.loginType === LoginType.Corporate) {
                this.isCorporateUser = true;
            }
        }

        this.route.queryParams.subscribe((params) => {
            this.request.returnUrl = Extensions.getReturnUrl(params)
                ? Extensions.getReturnUrl(params)
                : Extensions.returnUrl;
            Extensions.returnUrl = this.request.returnUrl;

            //if (params['username']) {
            // this.username = params['username'];
            //}
        });
    }

    public ngAfterViewInit(): void {
        this.setUpKeyListener();
        this.focusUsernameField();
    }

    public hasValidFields() {
        if (this.isUsernameInput) {
            if (this.username && this.username.trim().length > 4) return true;
            else return false;
        }
        if (!this.isUsernameInput) {
            if (this.password && this.password.length > 4) return true;
            else return false;
        }
    }

    public retailSelected() {
        Extensions.setSelectedLoginType(this._router, this.route, LoginType.Retail);
        this.variablesAtUsernameInput();
    }

    public corporateSelected() {
        Extensions.setSelectedLoginType(this._router, this.route, LoginType.Corporate);
        this.variablesAtUsernameInput();
    }

    public next(): void {
        GoogleAnalyticsUtils.sendContinueEvent();
        if (this.action === LoginAction.LinkUsers) {
            this.callLinkUserStatusService();
        } else if (this.action === LoginAction.ImpersonationLogin) {
            this.callImpersonationUserStatusService();
        } else {
            this.callUserStatusService();
            this.leaveDynatraceEvent(this.usernameEnterEventVal);
        };
    }

    public performLogin(): void {
        if (!this.hasValidFields()) return;
        this.isLoading = true;
        this.request.username = this.username;
        this.request.rememberLogin = this.rememberLogin;

        if (AppSettings.enforcePasswordEncryption) {
            const rsaKey = new RSAKey();
            rsaKey.setPublic(AppSettings.getPropertyN(), AppSettings.getPropertyE());
            this.request.password = rsaKey.encrypt(this.password);
        } else {
            this.request.password = this.password;
        }

        GoogleAnalyticsUtils.sendLoginEvent();
        AutoRefreshTimerService.lock();
        this.action === LoginAction.LinkUsers
            ? this.callLinkUserService()
            : this.callSignInService();
    }

    public backToFirstStep() {
        if (!this.isLoading && !this.isUsernameInput) {
            this.variablesAtUsernameInput(true);
            this.usernameEnterEventVal = null;
            this.passwordEnterEventVal = null;
            this.focusUsernameField();
        }
    }
    public forgotPasswordClicked(): void {
        this.setDynatraceEvent('Login_forgot_password');

        if (this.isCorporateBlockedFlowEnabled) this.isForgotPasswordClicked = true;
        if (this.isCorporateUser && !this.isCorporateBlockedFlowEnabled) {
            this.toggleBlocked();
            return;
        } else {
            this.retrievePassword()
        };
    }
    public forgotUsernameClicked(): void {
        this.setDynatraceEvent('Login_forgot_username_password');

        if (this.loginType == LoginType.Retail) {
            this.toggleForgetCredentialsRetaiModal();
        } else this.toggleBlocked();
    }

    public toggleVisibility() {
        this.isPasswordVisible = !this.isPasswordVisible;
    }

    public retrievePassword() {
        if (this.isCorporateUser && this.isBlocked && !this.isCorporateBlockedFlowEnabled) {
            this.isForgotPasswordLoading = false;
            this.toggleBlocked();
            return;
        }

        const request: IUserIDRequest = {
            UserID: this.username,
        };
        this.userData.userId = this.username;
        this.isForgotPasswordLoading = true;
        GetPersonAccountUnlockOptions.getPersonAccountUnlockOptions(request)
            .pipe(finalize(() => (this.isForgotPasswordLoading = false)))
            .subscribe(
                (result) => this.resolveForgotPasswordResponse(result),
                (error) => this.handleError(error)
            );
    }

    public resolveForgotPasswordResponse(response: IGetPersonAccountUnlockOptionsResponse) {
        this.userData.hasActiveCreditCard = response.hasActiveCreditCard;
        this.userData.canUnlockWithPersonalInformation = response.canUnlockWithPersonalInformation;

        if (response && response.emailAddress != null) {
            this.userData.email = response.emailAddress;
            this.userData.hasEmail = true;
            UserData.update(this.userData);
            if (this.isCorporateUser && this.isCorporateBlockedFlowEnabled) {
                let modalRef = this.modalService.open(CorporateRecoveryPasswordModalComponent, {
                    windowClass: 'modal-small',
                });
                modalRef.componentInstance.isCorporate = this.isCorporateUser;
                modalRef.componentInstance.blockedUser = this.isBlocked;
                modalRef.componentInstance.isForgotPasswordClicked = this.isForgotPasswordClicked;
            } else {
                this.modalService.open(PasswordRetrievalComponent, {
                    windowClass: 'modal-small',
                });
            }
        } else {
            if (this.isCorporateUser && this.isCorporateBlockedFlowEnabled) {
                this.isForgotPasswordLoading = false;
                this.toggleBlocked();
                return;
            }
            this.userData.hasEmail = false;
            UserData.update(this.userData);
            if (!response.hasActiveCreditCard && response.canUnlockWithPersonalInformation) {
                let modalRef = this.modalService.open(RestoreWithAuthenticationComponent, {
                    windowClass: !this.isLoginBigModalEnabled ? '' : 'modal-full',
                });
                modalRef.componentInstance.hasBackBtn = false;
                modalRef.componentInstance.isBlocked = this.isBlocked;
            }
            else {
                let modalRef = this.modalService.open(PasswordRetrievalComponent, {
                    windowClass: 'modal-small',
                });
                modalRef.componentInstance.hasBackBtn = false;
                modalRef.componentInstance.isBlocked = this.isBlocked;
            }

        }
    }

    public learnMore(type: string) {
        switch (type) {
            case 'register-nbg':
                if (this.dynatraceForExistingOnBoarding) this._setStartNewCustDynatraceEvent();
                if (this.langId === 'el') {
                    window.open(
                        'https://www.nbg.gr/el/idiwtes/kathimerines-sunallages/digital-banking/ksekiniste-me-thn-ethniki-online',
                        '_blank'
                    );
                } else {
                    window.open(
                        'https://www.nbg.gr/en/individuals/daily-transactions/online-account-opening/customer-onboarding',
                        '_blank'
                    );
                }
                break;
            case 'register-ibank':
                window.open('https://microsites.nbg.gr/mobilebanking/index_CO.html', '_blank');
                break;
        }
    }

    public toggleIBankCredentials(): void {
        if (this.dynatraceForExistingOnBoarding) this._setClickNotRegistDynatraceEvent();
        this.modalService.open(IBankCredentialsModalComponent, {
            windowClass: 'modal-small',
        });
    }

    public createNewUser(status: string) {
        let modaleRef = this.modalService.open(CreatePasswordComponent, {
            windowClass: !this.isLoginBigModalEnabled ? '' : 'modal-full',
        });
        modaleRef.componentInstance.isNewUser = status === Constants.New_Status;
        modaleRef.componentInstance.userCode = this.username;
        modaleRef.componentInstance.hasBackBtn = false;
    }

    private toggleSwitchModal(
        userStatusResult: IUserStatusResponse | IImpersonationUserStatusResponse
    ) {
        const modalRef = this.modalService.open(SwitchLoginTransitionModalComponent, {
            windowClass: 'modal-xs',
        });
        (modalRef.componentInstance as SwitchLoginTransitionModalComponent).loginType =
            this.loginType;

        if (!!userStatusResult) {
            modalRef.result.then((modalResult) => {
                if (modalResult === true) {
                    this.handleUserStatus(userStatusResult);
                }
            });
        }
    }

    private toggleSelfBlockedhModal() {
        const modalRef = this.modalService.open(SelfBlockedModalComponent, {
            windowClass: 'modal-xs',
        });
    }

    private toggleForgetCredentialsRetaiModal() {
        this.modalService.open(ForgetCredentialsRetailModalComponent, {
            windowClass: 'modal-small',
        });
    }

    private toggleBlocked() {
        const modalRef = this.modalService.open(BlockedBusinessModalComponent, {
            windowClass: 'modal-small',
        });
        (modalRef.componentInstance as BlockedBusinessModalComponent).blockedUser = this.isBlocked;
        (modalRef.componentInstance as BlockedBusinessModalComponent).isForgotPasswordClicked =
            this.isForgotPasswordClicked;
    }
    public toggleBusinessRegistrationInfoModal() {
        this.modalService.open(BusinessRegistrationInfoModalComponent, {
            windowClass: 'modal-small',
        });
    }
    private callLinkUserService(): void {
        LinkUsersService.initiateUserLink(this.request)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe(
                (result) => this.handleLinkUserResponse(result),
                (error) => {
                    AutoRefreshTimerService.unlock();
                    if (error.desc === 'password-blocked') {
                        this.isBlocked = true;
                        this.retrievePassword();
                        return;
                    }
                    this.handleError(error);
                }
            );
    }
    private handleLinkUserResponse(response: any): void {
        this.modalService.open(ConnectProfileModalComponent, {
            windowClass: !this.isGroupUserIdsBigModalEnabled ? 'modal-small' : 'modal-full',
        });
        this.variablesAtUsernameInput();
    }
    private variablesAtUsernameInput(calledFromBackToFirstStep: boolean = false) {
        if (calledFromBackToFirstStep) {
            this.username = null;
            this.isUsernameInput = true;
        } else {
            this.username = this.loginHint;
            this.isUsernameInput = this.loginHint == null;
        }

        this.password = null;
        this.isCorporateUser = false;
        this.isBlocked = false;
        this.usernameRecovery = true;
    }

    private callSignInService(): void {
        this.leaveDynatraceEvent(this.passwordEnterEventVal);

        UserAuth.signIn(this.request)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe(
                (result) => this.handleSignInResponse(result),
                (error) => {
                    AutoRefreshTimerService.unlock();
                    if (error.desc === 'password-blocked') {
                        this.isBlocked = true;
                        this.retrievePassword();
                    } else {
                        if (
                            error.desc === 'password-expired' &&
                            AppSettings.isNewChangePasswordModals
                        ) {
                            this.password = null;
                            UserData.update({ userId: this.username });
                            this.focusPasswordField();
                            this.handleError(error);
                        } else if (error.desc === 'Self-locked user' && this.isBlockUserEnabled) {
                            this.toggleSelfBlockedhModal();
                        } else this.handleError(error);
                    }
                }
            );
    }

    private callLinkUserStatusService(): void {
        this.isLoading = true;
        LinkUsersService.checkUserStatus(this.username)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe(
                (result) => this.handleUserStatusResponse(result),
                (error) => this.handleError(error)
            );
    }

    private needsSwitchUserLogin(): boolean {
        return (
            (this.loginType == LoginType.Corporate && !this.isCorporateUser) ||
            (this.loginType == LoginType.Retail && this.isCorporateUser)
        );
    }

    private handleUserStatusResponse(
        result: IUserStatusResponse | IImpersonationUserStatusResponse
    ): void {
        if (this.needsSwitchUserLogin()) {
            this.toggleSwitchModal(result);
        } else {
            this.handleUserStatus(result);
        }
    }

    private handleUserStatus(result: IUserStatusResponse | IImpersonationUserStatusResponse) {
        this.isBlocked = result.userStatus === Constants.Blocked_Status;
        const preferredUx = (Array.isArray(result.ux) && result.ux.length) > 0 ? result.ux.find((ux: IPreferredUserExperience) => ux.sourceClientId === this.getLoginResponse.clientId) : null;
        if (this.isBlocked) {
            this.isUsernameInput = true;
            this.retrievePassword();
        } else if (
            result.userStatus === Constants.New_Status ||
            result.userStatus === Constants.Init_Status
        ) {
            this.isUsernameInput = true;
            this.createNewUser(result.userStatus);
        } else if (preferredUx && preferredUx.redirectUrl) {
            const redirectUri = `${preferredUx.redirectUrl}?login_hint=${encodeURIComponent(this.username)}`;
            window.location.href = redirectUri;
        }
        else {
            if (this.action === LoginAction.ImpersonationLogin) {
                const modalRef = this.modalService.open(TwoFAModalComponent, {
                    windowClass: 'modal-small',
                });
                (modalRef.componentInstance as TwoFAModalComponent).twoFAInfo =
                    new TwoFactorAuthenticationInfo(
                        (result as IImpersonationUserStatusResponse).additionalAuthenticationInfo
                    );
            } else {
                this.isUsernameInput = false;
                this.usernameRecovery = false;
                this.focusPasswordField();
            }
        }
    }

    private callImpersonationUserStatusService(): void {
        this.isUsernameInput = true;
        this.isLoading = true;
        ImpersonationService.checkUserStatus(this.username)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe(
                (result) => this.handleStatusResponse(result),
                (error) => this.handleError(error)
            );
    }

    private handleStatusResponse(result: IImpersonationUserStatusResponse): void {
        this.isLoading = false;
        if (this.needsSwitchUserLogin()) {
            this.toggleSwitchModal(result);
        } else if (!!result && !!result.additionalAuthenticationInfo) {
            this.goToCodeVerification(result.additionalAuthenticationInfo);
        }
    }

    private callUserStatusService(isRetry: boolean = false): void {
        this.isLoading = true;
        UserAuth.checkUserStatus(this.username)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe(
                (result) => this.handleUserStatusResponse(result),
                (error) => {
                    if (error && error.code === 'EMPTY_RESPONSE') {
                        if (!isRetry) {
                            this.callUserStatusService(true);
                        }
                        return;
                    }
                    this.handleError(error);
                }
            );
    }

    private handleSignInResponse(response: ITwoFactorAuthenticationInfo) {
        this.isLoading = false;

        if (!!response) {
            this.setDynatraceEvent('Login_successful');
            this.goToCodeVerification(response);
        }
    }
    private handleError(error: IErrorDescription): void {
        this.isLoading = false;
        this.setDynatraceEvent('Login_unsuccessful');
        ErrorHandler.newError(error);
    }

    private goToCodeVerification(twofaInfo: ITwoFactorAuthenticationInfo): void {
        const modalRef = this.modalService.open(TwoFAModalComponent, {
            windowClass: 'modal-small',
        });
        (modalRef.componentInstance as TwoFAModalComponent).twoFAInfo =
            new TwoFactorAuthenticationInfo(twofaInfo);
    }

    private setUpKeyListener(): void {
        this.elRef.nativeElement
            .querySelector('.user-login-subcontainer')
            .addEventListener('keydown', (e: any) => {
                let key = e.which || e.keyCode;
                if (key === Constants.KEY_ENTER) {
                    if (this.isUsernameInput) {
                        this.next();

                        this.preventEventPropagation(e);
                    } else if (!this.isUsernameInput) {
                        this.performLogin();
                        this.preventEventPropagation(e);
                    }
                }
            });
    }

    private preventEventPropagation(e: any) {
        e.stopPropagation();
        e.preventDefault();
    }

    private focusUsernameField() {
        setTimeout(() => {
            !!this.usernameRef &&
                !!this.usernameRef.nativeElement &&
                this.usernameRef.nativeElement.focus();
        }, 500);
    }

    public onUsernameInput(): void {
        if (this.username && this.username.length === 1 && !this.usernameEnterEventVal) {
            this.usernameEnterEventVal = this.enterDynatraceEvent('Login_username');
        }
    }

    private focusPasswordField(): void {
        setTimeout(() => {
            !!this.passwordRef &&
                !!this.passwordRef.nativeElement &&
                this.passwordRef.nativeElement.focus();
        }, 500);
    }

    public onPasswordInput(): void {
        if (this.password && this.password.length === 1 && !this.passwordEnterEventVal) {
            this.passwordEnterEventVal = this.enterDynatraceEvent('Login_password');
        }
    }

    private initializeSignInRequest() {
        this.request = {
            username: undefined,
            password: undefined,
            rememberLogin: undefined,
            returnUrl: undefined,
            userID: undefined,
        };
    }
    private _initSubscriptions(): void {
        this.mortalize(this._langSvc.activeLanguage$).subscribe((lang) => (this.langId = lang));
    }

    private _setClickNotRegistDynatraceEvent() {
        this.setDynatraceEvent('ECO_Login_Page_Not_Registered');
    }
    private _setStartNewCustDynatraceEvent() {
        this.setDynatraceEvent('ECO_Login_Page_New_Cust');
    }
}
